import { X, ChevronLeft, ChevronRight, Star, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';
import { Photo } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface FullscreenImageViewerProps {
  photo: Photo;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
  onPhotoUpdate?: (photo: Photo) => void;
}

export function FullscreenImageViewer({ 
  photo, 
  onClose, 
  onNext, 
  onPrevious,
  hasNext = false,
  hasPrevious = false,
  onPhotoUpdate
}: FullscreenImageViewerProps) {
  const [rating, setRating] = useState(photo.rating || 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [isFavorite, setIsFavorite] = useState(photo.is_favorite || false);
  
  useEffect(() => {
    setRating(photo.rating || 0);
    setIsFavorite(photo.is_favorite || false);
  }, [photo.id, photo.rating, photo.is_favorite]);
  
  const handleRating = async (newRating: number) => {
    const finalRating = newRating === rating ? 0 : newRating;
    setRating(finalRating);

    const { error } = await supabase
      .from('photos')
      .update({ rating: finalRating })
      .eq('id', photo.id);

    if (error) {
      toast.error('Failed to update rating');
      setRating(rating); // Revert on error
    } else {
      toast.success(`Rated ${finalRating} star${finalRating !== 1 ? 's' : ''}`);
      if (onPhotoUpdate) {
        onPhotoUpdate({ ...photo, rating: finalRating });
      }
    }
  };

  const handleToggleFavorite = async () => {
    const newFavorite = !isFavorite;
    setIsFavorite(newFavorite);

    const { error } = await supabase
      .from('photos')
      .update({ is_favorite: newFavorite })
      .eq('id', photo.id);

    if (error) {
      toast.error('Failed to update favorite');
      setIsFavorite(isFavorite);
    } else {
      toast.success(newFavorite ? 'Added to favorites' : 'Removed from favorites');
      if (onPhotoUpdate) {
        onPhotoUpdate({ ...photo, is_favorite: newFavorite });
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight' && hasNext && onNext) {
        onNext();
      } else if (e.key === 'ArrowLeft' && hasPrevious && onPrevious) {
        onPrevious();
      } else if (['1', '2', '3', '4', '5'].includes(e.key)) {
        handleRating(parseInt(e.key));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNext, onPrevious, hasNext, hasPrevious, rating, isFavorite]);

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      {/* Close button */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 z-10 text-white hover:bg-white/10"
        onClick={onClose}
      >
        <X className="w-6 h-6" />
      </Button>

      {/* Previous button */}
      {hasPrevious && onPrevious && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            onPrevious();
          }}
        >
          <ChevronLeft className="w-8 h-8" />
        </Button>
      )}

      {/* Next button */}
      {hasNext && onNext && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/10"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
        >
          <ChevronRight className="w-8 h-8" />
        </Button>
      )}

      {/* Image container */}
      <div className="w-full h-full flex flex-col items-center justify-center p-4 gap-6">
        <img
          src={photo.url}
          alt={photo.filename}
          className="max-w-full max-h-[calc(100vh-200px)] object-contain animate-scale-in"
          onClick={(e) => e.stopPropagation()}
        />

        {/* Rating and Favorite */}
        <div 
          className="flex items-center gap-4 bg-black/70 backdrop-blur-sm px-6 py-3 rounded-lg"
          onClick={(e) => e.stopPropagation()}
          onMouseLeave={() => setHoverRating(0)}
        >
          {/* Favorite heart */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleToggleFavorite}
            className="h-10 w-10 hover:bg-white/10"
          >
            <Heart 
              className={`w-6 h-6 transition-all ${
                isFavorite 
                  ? 'fill-red-500 text-red-500' 
                  : 'text-white/50 hover:text-red-500'
              }`}
            />
          </Button>

          {/* Divider */}
          <div className="w-px h-8 bg-white/30" />

          {/* Star rating */}
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`w-8 h-8 cursor-pointer transition-all ${
                  star <= (hoverRating || rating)
                    ? 'fill-yellow-400 text-yellow-400 scale-110'
                    : 'text-white/50 hover:text-yellow-400/70'
                }`}
                onClick={() => handleRating(star)}
                onMouseEnter={() => setHoverRating(star)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Image info */}
      <div 
        className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-sm px-4 py-2 rounded-lg text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-sm font-medium">{photo.filename}</p>
        <p className="text-xs text-white/70">
          {photo.dimensions.width} × {photo.dimensions.height}
          {rating > 0 && <span className="ml-2">• {rating} ★</span>}
        </p>
      </div>
    </div>
  );
}
